import React, { ReactNode, useEffect, useState } from 'react';
import { collection, getDocs, limit, query, where } from 'firebase/firestore';
import { CalendarClock, Wrench, X } from 'lucide-react';
import { db } from '../../firebase';
import { useAuth } from '../../hooks/useAuth';
import { useMaintenance } from '../../hooks/useMaintenance';
import { LoadingSpinner } from '../LoadingSpinner';
import { MaintenancePage } from './MaintenancePage';
import { MAINTENANCE_SETTINGS_PATH, formatWaktuWIB } from '../../utils/maintenance';

const PENGUMUMAN_DITUTUP_KEY = 'mias_maintenance_pengumuman_ditutup';

/** Role dari Firestore user_permissions, sama dengan sumber isSuperAdmin di UserAccessContext. */
const useIsSuperAdmin = (email: string | null | undefined, perluCek: boolean) => {
  const [hasil, setHasil] = useState<{ email: string; superAdmin: boolean } | null>(null);

  useEffect(() => {
    if (!email || !perluCek || hasil?.email === email) return;
    let batal = false;
    getDocs(query(collection(db, 'user_permissions'), where('email', '==', email.toLowerCase()), limit(1)))
      .then((snap) => {
        if (!batal) setHasil({ email, superAdmin: !snap.empty && snap.docs[0].data()?.role === 'super_admin' });
      })
      .catch((e) => {
        console.warn('[Maintenance] Gagal cek role:', e);
        if (!batal) setHasil({ email, superAdmin: false });
      });
    return () => { batal = true; };
  }, [email, perluCek, hasil?.email]);

  if (!email) return { superAdmin: false, memuat: false };
  if (hasil?.email !== email) return { superAdmin: false, memuat: perluCek };
  return { superAdmin: hasil.superAdmin, memuat: false };
};

/**
 * Membungkus seluruh aplikasi. Saat Mode Maintenance berlangsung, semua halaman
 * (termasuk login, kiosk, dan halaman publik) diganti halaman maintenance,
 * kecuali untuk akun Super Admin. Lihat docs/PANDUAN_MODE_MAINTENANCE.md.
 */
export const MaintenanceGate: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { authUser, login, logout } = useAuth();
  const { pengaturan, status, siap, muatUlang } = useMaintenance();
  const berlangsung = status === 'berlangsung';
  const { superAdmin, memuat: memuatRole } = useIsSuperAdmin(authUser?.email, berlangsung);

  const [pengumumanDitutup, setPengumumanDitutup] = useState<string | null>(() => {
    try { return sessionStorage.getItem(PENGUMUMAN_DITUTUP_KEY); } catch { return null; }
  });

  if (!siap) return <LoadingSpinner />;

  if (berlangsung) {
    if (authUser && memuatRole) return <LoadingSpinner />;

    if (!superAdmin) {
      return (
        <MaintenancePage
          pengaturan={pengaturan}
          onCobaLagi={muatUlang}
          emailMasuk={authUser?.email ?? null}
          onKeluar={async () => {
            await logout();
            window.location.hash = '#/';
          }}
          onLoginAdmin={async (email, password) => {
            await login(email, password, true);
          }}
        />
      );
    }

    return (
      <>
        {children}
        <a
          href={`#${MAINTENANCE_SETTINGS_PATH}`}
          className="fixed bottom-4 left-4 z-[9999] inline-flex items-center gap-2 rounded-full bg-amber-500 px-3.5 py-2 text-xs font-bold text-white shadow-lg ring-2 ring-white hover:bg-amber-600"
          title="Buka pengaturan Mode Maintenance"
        >
          <Wrench className="h-4 w-4" /> Maintenance aktif
        </a>
      </>
    );
  }

  // Pengumuman maintenance yang akan datang, ditampilkan 24 jam sebelumnya.
  const kunciPengumuman = pengaturan ? `${pengaturan.mulai}|${pengaturan.updated_at}` : null;
  const mulaiMs = pengaturan?.mulai ? new Date(pengaturan.mulai).getTime() : null;
  const tampilPengumuman =
    status === 'terjadwal' &&
    mulaiMs !== null &&
    mulaiMs - Date.now() <= 24 * 60 * 60 * 1000 &&
    pengumumanDitutup !== kunciPengumuman;

  return (
    <>
      {children}
      {tampilPengumuman && (
        <div className="fixed inset-x-0 bottom-0 z-[9999] px-3 pb-3 sm:bottom-4 sm:left-auto sm:right-4 sm:max-w-sm sm:px-0 sm:pb-0">
          <div className="flex items-start gap-3 rounded-2xl bg-[#174143] p-3.5 text-white shadow-2xl ring-1 ring-white/10">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-amber-400 text-[#174143]">
              <CalendarClock className="h-5 w-5" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-bold">Maintenance terjadwal</p>
              <p className="text-xs leading-relaxed text-teal-50/85">
                Aplikasi tidak bisa dipakai mulai {formatWaktuWIB(pengaturan?.mulai ?? null)}. Simpan pekerjaan Anda sebelum waktu itu.
              </p>
            </div>
            <button
              type="button"
              onClick={() => {
                setPengumumanDitutup(kunciPengumuman);
                try { if (kunciPengumuman) sessionStorage.setItem(PENGUMUMAN_DITUTUP_KEY, kunciPengumuman); } catch { /* abaikan */ }
              }}
              className="rounded-full p-1 text-teal-100/70 hover:bg-white/10"
              aria-label="Tutup pengumuman"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default MaintenanceGate;
